import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Empty,
  Radio,
  Row,
  Space,
  Spin,
  Table,
  Tabs,
  TabPane,
  Tag,
  Toast,
  Typography,
  SideSheet,
  Descriptions,
  TreeSelect,
  Progress,
  Tooltip,
} from "@douyinfe/semi-ui";
import {
  IconRefresh,
  IconDownload,
  IconCalendar,
  IconPlay,
  IconPhone,
  IconTick,
  IconAlertCircle,
  IconHistogram,
  IconPieChartStroked,
  IconSearch,
  IconFilter,
} from "@douyinfe/semi-icons";
import { VChart } from "@visactor/react-vchart";
import dayjs from "dayjs";
import {
  ApiClient,
  CustomerTouchDetailVO,
  CustomerTouchStatReq,
  CustomerTouchStatVO,
  LeadCallLogItem,
} from "../../api/ApiClient";
import { formatDateTime } from "../../utils";
import AudioPlayerModal from "../../comp/AudioPlayerModal";

const { Title, Text, Paragraph } = Typography;
const apiClient = ApiClient.getDefault();

// ===== 常量 =====
const PAGE_SIZE = 20;
const CALL_LOG_PAGE_SIZE = 50;
const EFFECTIVE_THRESHOLD_SECONDS = 20;

// 状态字典
const callStatusMap: Record<string, string> = {
  "CALLING": "拨号中",
  "CONNECTED": "已接通",
  "ENDED": "已接通",
  "MISSED": "未接通",
};

// 配色方案
const COLORS = {
  primary: "#3370ff",
  success: "#34c724",
  warning: "#ff7d00",
  danger: "#f5222d",
  purple: "#722ed1",
  cyan: "#13c2c2",
  bgCard: "var(--semi-color-bg-1)",
  bgPage: "var(--semi-color-bg-0)",
  border: "var(--semi-color-border)",
  textPrimary: "var(--semi-color-text-0)",
  textSecondary: "var(--semi-color-text-1)",
  textTertiary: "var(--semi-color-text-2)",
};

// ===== 工具函数 =====
function formatDuration(sec: number): string {
  if (sec == null || isNaN(sec)) return "-";
  if (sec < 60) return `${Math.round(sec)}秒`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}分${s}秒`;
}

function percentText(rate: number): string {
  if (rate == null || isNaN(rate)) return "-";
  return `${rate.toFixed(1)}%`;
}

function translateStatus(status: string): string {
  if (!status) return "-";
  return callStatusMap[status.toUpperCase()] || status;
}

// 定位以指定节点为根的子树
function findSubtree(nodes: any[], targetId: number): any[] | null {
  for (const n of nodes) {
    if (Number(n.value) === targetId) return [n];
    if (n.children?.length) {
      const found = findSubtree(n.children, targetId);
      if (found) return found;
    }
  }
  return null;
}

// 收集子树全部成员 id（含根节点）
function collectSubtreeIds(nodes: any[]): number[] {
  const ids: number[] = [];
  const walk = (list: any[]) => list.forEach((n) => {
    ids.push(Number(n.value));
    if (n.children?.length) walk(n.children);
  });
  walk(nodes);
  return ids;
}

// ===== 自定义组件：KPI卡片 =====
// 布局：左侧两个数量数字并排突出（分子语义色、分母主色），右侧环形进度展示比率
// 百分比只在圆环中心出现，不再重复展示大号率值
function KpiCard({
  title,
  icon,
  color,
  hit,
  hitLabel,
  total,
  totalLabel,
  ring,
  style,
}: {
  title: string;
  icon: React.ReactNode;
  color: string;
  hit: number;
  hitLabel: string;
  total: number;
  totalLabel: string;
  ring?: number;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={{
        background: COLORS.bgCard,
        borderRadius: 12,
        padding: "14px 16px",
        border: `1px solid ${COLORS.border}`,
        display: "flex",
        alignItems: "center",
        gap: 12,
        ...style,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <div
            style={{
              width: 26,
              height: 26,
              borderRadius: 6,
              background: `${color}15`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: color,
            }}
          >
            {icon}
          </div>
          <Text style={{ color: COLORS.textSecondary, fontSize: 13 }}>{title}</Text>
        </div>

        {/* 两个数量并排突出展示 */}
        <div style={{ display: "flex", alignItems: "flex-end", gap: 24 }}>
          <div>
            <div style={{ fontSize: 24, fontWeight: 700, color: color, lineHeight: 1 }}>{hit}</div>
            <div style={{ fontSize: 11, color: COLORS.textTertiary, marginTop: 4 }}>{hitLabel}</div>
          </div>
          <div>
            <div style={{ fontSize: 24, fontWeight: 700, color: COLORS.textPrimary, lineHeight: 1 }}>{total}</div>
            <div style={{ fontSize: 11, color: COLORS.textTertiary, marginTop: 4 }}>{totalLabel}</div>
          </div>
        </div>
      </div>

      {/* 环形进度：唯一展示百分比的位置 */}
      {ring != null && <ProgressRing percent={ring} size={64} color={color} />}
    </div>
  );
}

// ===== 自定义组件：KPI 卡片行 =====
function KpiSection({ title, cards }: { title: string; cards: any[] }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 600, color: COLORS.textSecondary }}>
        {title}
      </div>
      <Row gutter={[12, 12]}>
        {cards.map((card, i) => (
          <Col key={i} span={8}>
            <KpiCard {...card} />
          </Col>
        ))}
      </Row>
    </div>
  );
}

// ===== 自定义组件：进度环 =====
function ProgressRing({ percent, size = 60, color }: { percent: number; size?: number; color: string }) {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (percent / 100) * circumference;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* 背景环 */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="var(--semi-color-fill-1)"
        strokeWidth="6"
      />
      {/* 进度环 */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth="6"
        strokeLinecap="round"
        strokeDasharray={strokeDasharray}
        strokeDashoffset={strokeDashoffset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: "stroke-dashoffset 0.6s ease" }}
      />
      {/* 中心文字 */}
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontSize: size * 0.22, fontWeight: 700, fill: COLORS.textPrimary }}
      >
        {percent.toFixed(0)}%
      </text>
    </svg>
  );
}

// ===== 主组件 =====
export default function CustomerTouchStat() {
  // ===== 状态 =====
  const today = dayjs();
  const [dateRange, setDateRange] = useState<[Date, Date]>([today.toDate(), today.toDate()]);
  const [teamId, setTeamId] = useState<number | null>(null);
  // 所选销售（树节点根）；查询时展开为"本人 + 全部下级"，与树形口径一致
  const [saleId, setSaleId] = useState<number | null>(null);

  const [filterMode, setFilterMode] = useState<"total" | "new" | "old" | "compare">("total");
  const [activeTab, setActiveTab] = useState<"customer" | "sales">("customer");

  const [appliedReq, setAppliedReq] = useState<CustomerTouchStatReq>({
    callAtStart: today.format("YYYY-MM-DD"),
    callAtEnd: today.format("YYYY-MM-DD"),
  });

  const [statData, setStatData] = useState<CustomerTouchStatVO | null>(null);
  const [statLoading, setStatLoading] = useState(false);
  const [detailList, setDetailList] = useState<CustomerTouchDetailVO[]>([]);
  const [detailTotal, setDetailTotal] = useState(0);
  const [detailPage, setDetailPage] = useState(1);
  const [detailLoading, setDetailLoading] = useState(false);

  const [teamTreeData, setTeamTreeData] = useState<any[]>([]);
  const [managerTreeData, setManagerTreeData] = useState<any[]>([]);

  const [drillLead, setDrillLead] = useState<{
    leadId: number;
    saleId: number | null;
    leadName: string;
    connected: boolean | null; // null=全部，true=仅接通，false=仅未接通
  } | null>(null);
  const [callLogs, setCallLogs] = useState<LeadCallLogItem[]>([]);
  const [callLogsTotal, setCallLogsTotal] = useState(0);
  const [callLogsPage, setCallLogsPage] = useState(1);
  const [callLogsLoading, setCallLogsLoading] = useState(false);

  const [audioVisible, setAudioVisible] = useState(false);
  const [audioUrl, setAudioUrl] = useState("");
  const [audioTitle, setAudioTitle] = useState("");
  const [audioFileName, setAudioFileName] = useState("");

  // ===== 初始化 =====
  useEffect(() => {
    window.document.title = "客户触达分析";
    apiClient.getTeamAsOpts().then(setTeamTreeData).catch(() => {});
    const role = localStorage.getItem("role");
    const username = localStorage.getItem("username");
    const currUsername = ["op_manager", "monitor", "foreground"].includes(role || "") ? "root" : username;
    apiClient.getTeamMemberTreeAsOptsOfAny(currUsername).then(setManagerTreeData).catch(() => {});
  }, []);

  // ===== 数据加载 =====
  const loadStat = async (req: CustomerTouchStatReq) => {
    setStatLoading(true);
    try {
      const resp = await apiClient.getCustomerTouchStat(req);
      setStatData(resp.data);
    } catch (e) {
      Toast.error("数据加载失败");
    } finally {
      setStatLoading(false);
    }
  };

  const loadDetail = async (req: CustomerTouchStatReq, page: number) => {
    setDetailLoading(true);
    try {
      const resp = await apiClient.getCustomerTouchDetail(req, page, PAGE_SIZE);
      setDetailList(resp.data?.records ?? []);
      setDetailTotal(resp.data?.total ?? 0);
      setDetailPage(page);
    } catch (e) {
      Toast.error("明细加载失败");
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    loadStat(appliedReq);
    loadDetail(appliedReq, 1);
  }, [appliedReq]);

  // ===== 操作 =====
  // 所选销售展开为"本人 + 全部下级"id 列表；树里找不到时至少按本人过滤，避免退化为查全部
  // 注意：树节点 value 是字符串（user.id + ""），TreeSelect 回传也是字符串，比较前必须转数字
  const collectSaleIds = (): number[] | undefined => {
    if (saleId == null || managerTreeData.length === 0) return undefined;
    const subtree = findSubtree(managerTreeData, saleId);
    if (!subtree) return [saleId];
    const ids = collectSubtreeIds(subtree);
    return ids.length > 0 ? ids : [saleId];
  };

  const buildAppliedReq = (
    start: Date,
    end: Date,
    mode: typeof filterMode,
  ): CustomerTouchStatReq => ({
    callAtStart: dayjs(start).format("YYYY-MM-DD"),
    callAtEnd: dayjs(end).format("YYYY-MM-DD"),
    teamId: teamId ?? undefined,
    saleIds: collectSaleIds(),
    customerType: mode === "new" || mode === "old" ? mode : undefined,
  });

  const handleFilter = () => {
    const [start, end] = dateRange;
    setAppliedReq(buildAppliedReq(start, end, filterMode));
  };

  // 筛选模式切换后立即查询，保持图表（客户端切片）与明细数据同步
  const handleFilterModeChange = (mode: "total" | "new" | "old" | "compare") => {
    setFilterMode(mode);
    const [start, end] = dateRange;
    setAppliedReq(buildAppliedReq(start, end, mode));
  };

  const handleReset = () => {
    const now = dayjs();
    setDateRange([now.toDate(), now.toDate()]);
    setTeamId(null);
    setSaleId(null);
    setFilterMode("total");
    setAppliedReq({
      callAtStart: now.format("YYYY-MM-DD"),
      callAtEnd: now.format("YYYY-MM-DD"),
    });
  };

  // 快捷日期选中即查询（不只是回填日期输入框）
  const handlePreset = (kind: "today" | "7d" | "month" | "30d") => {
    const now = dayjs();
    const presets: Record<string, [dayjs.Dayjs, dayjs.Dayjs]> = {
      today: [now, now],
      "7d": [now.subtract(6, "day"), now],
      "30d": [now.subtract(29, "day"), now],
      month: [now.startOf("month"), now],
    };
    const [start, end] = presets[kind];
    setDateRange([start.toDate(), end.toDate()]);
    setAppliedReq(buildAppliedReq(start.toDate(), end.toDate(), filterMode));
  };

  const handleExport = async () => {
    try {
      const url = await apiClient.getCustomerTouchDetailExportUrl(appliedReq);
      window.open(url, "_blank");
    } catch (e) {
      Toast.error("导出失败");
    }
  };

  const handleDrillDown = async (record: CustomerTouchDetailVO, connected: boolean | null = null) => {
    setDrillLead({ leadId: record.leadId, saleId: record.saleId, leadName: record.leadName, connected });
    setCallLogs([]);
    setCallLogsPage(1);
    await loadCallLogs(record.leadId, record.saleId, 1, connected);
  };

  const loadCallLogs = async (leadId: number, saleId: number | null, page: number, connected: boolean | null = null) => {
    setCallLogsLoading(true);
    try {
      const resp = await apiClient.getCustomerTouchCallLogs(leadId, saleId, appliedReq, page, CALL_LOG_PAGE_SIZE, connected);
      setCallLogs(resp.data?.records ?? []);
      setCallLogsTotal(resp.data?.total ?? 0);
      setCallLogsPage(page);
    } catch (e) {
      Toast.error("通话记录加载失败");
    } finally {
      setCallLogsLoading(false);
    }
  };

  // ===== 计算数据 =====
  const summary = statData?.summary;

  const kpiData = useMemo(() => {
    if (!summary) return { assign: [], dialed: [] };

    // 卡片主体是两个数量（hit/total），比率只通过圆环展示
    const assign = [
      {
        title: "分配触达",
        hit: summary.totalAssignedTouched,
        hitLabel: "已触达",
        total: summary.totalAssigned,
        totalLabel: "总分配",
        ring: summary.assignedTouchRate,
        icon: <IconTick />,
        color: COLORS.primary,
      },
      {
        title: "新客触达",
        hit: summary.newAssignedTouched,
        hitLabel: "已触达",
        total: summary.newAssignedCount,
        totalLabel: "新客分配",
        ring: summary.newAssignedTouchRate,
        icon: <IconTick />,
        color: COLORS.warning,
      },
      {
        title: "老客触达",
        hit: summary.oldAssignedTouched,
        hitLabel: "已触达",
        total: summary.oldAssignedCount,
        totalLabel: "老客分配",
        ring: summary.oldAssignedTouchRate,
        icon: <IconTick />,
        color: COLORS.success,
      },
    ];

    const dialed = [
      {
        title: "有效触达",
        hit: summary.effectiveDialedCount,
        hitLabel: "有效",
        total: summary.dialedCount,
        totalLabel: "拨号客户",
        ring: summary.effectiveDialedRate,
        icon: <IconPhone />,
        color: COLORS.primary,
      },
      {
        title: "新客有效",
        hit: summary.newEffectiveDialed,
        hitLabel: "有效",
        total: summary.newDialedCount,
        totalLabel: "新客拨号",
        ring: summary.newEffectiveDialedRate,
        icon: <IconPhone />,
        color: COLORS.warning,
      },
      {
        title: "老客有效",
        hit: summary.oldEffectiveDialed,
        hitLabel: "有效",
        total: summary.oldDialedCount,
        totalLabel: "老客拨号",
        ring: summary.oldEffectiveDialedRate,
        icon: <IconPhone />,
        color: COLORS.success,
      },
    ];

    return { assign, dialed };
  }, [summary]);

  // ===== 图表配置 =====
  // 返回 { spec, centerValue, centerLabel }：环形图模式下 centerValue 非空，渲染中心总数
  const customerAssignChart = useMemo(() => {
    if (!summary) return null;

    if (filterMode === "compare") {
      // 分组柱状图
      return {
        centerValue: null as number | null,
        centerLabel: "",
        spec: {
          type: "bar",
          data: [{
            id: "data",
            values: [
              { type: "总分配", metric: "已触达", value: summary.totalAssignedTouched },
              { type: "总分配", metric: "未触达", value: summary.totalAssigned - summary.totalAssignedTouched },
              { type: "新客", metric: "已触达", value: summary.newAssignedTouched },
              { type: "新客", metric: "未触达", value: summary.newAssignedCount - summary.newAssignedTouched },
              { type: "老客", metric: "已触达", value: summary.oldAssignedTouched },
              { type: "老客", metric: "未触达", value: summary.oldAssignedCount - summary.oldAssignedTouched },
            ],
          }],
          xField: ["type", "metric"],
          yField: "value",
          seriesField: "metric",
          bar: { style: { cornerRadius: 4 } },
          color: [COLORS.success, COLORS.danger],
          axes: [
            { orient: "left", title: { visible: true, text: "数量" } },
            { orient: "bottom" },
          ],
          // 柱顶直接标注数值，无需 hover
          label: { visible: true, position: "top", style: { fontSize: 10, fill: "#999" } },
          tooltip: { visible: true },
          legend: { visible: true, position: "top" },
        } as any,
      };
    }

    // 环形图（donut）：切片标签预拼"名称 数量 (百分比)"，中心显示总数
    let touched = 0, untouched = 0, centerLabel = "总分配";
    if (filterMode === "total") {
      touched = summary.totalAssignedTouched;
      untouched = summary.totalAssigned - summary.totalAssignedTouched;
    } else if (filterMode === "new") {
      touched = summary.newAssignedTouched;
      untouched = summary.newAssignedCount - summary.newAssignedTouched;
      centerLabel = "新客分配";
    } else {
      touched = summary.oldAssignedTouched;
      untouched = summary.oldAssignedCount - summary.oldAssignedTouched;
      centerLabel = "老客分配";
    }
    const total = touched + untouched;
    const pct = (v: number) => (total > 0 ? ((v / total) * 100).toFixed(1) : "0.0");

    return {
      centerValue: total,
      centerLabel,
      spec: {
        type: "pie",
        data: [{
          id: "data",
          values: [
            { type: `已触达 ${touched} (${pct(touched)}%)`, value: touched },
            { type: `未触达 ${untouched} (${pct(untouched)}%)`, value: untouched },
          ].filter(d => d.value > 0),
        }],
        valueField: "value",
        categoryField: "type",
        // 注意：VChart 1.13 的 innerRadius/outerRadius 是布局半径的比例(0~1)，不是像素：
        // 渲染时 layoutRadius * innerRadius（见 vchart polar.js），默认 outerRadius=0.6
        innerRadius: 0.4,
        color: [COLORS.success, COLORS.danger],
        label: { visible: true, position: "outside" },
        tooltip: { visible: true },
      } as any,
    };
  }, [summary, filterMode]);

  const customerDialedChart = useMemo(() => {
    if (!summary) return null;

    if (filterMode === "compare") {
      return {
        centerValue: null as number | null,
        centerLabel: "",
        spec: {
          type: "bar",
          data: [{
            id: "data",
            values: [
              { type: "总拨号", metric: "有效", value: summary.effectiveDialedCount },
              { type: "总拨号", metric: "无效", value: summary.dialedCount - summary.effectiveDialedCount },
              { type: "新客", metric: "有效", value: summary.newEffectiveDialed },
              { type: "新客", metric: "无效", value: summary.newDialedCount - summary.newEffectiveDialed },
              { type: "老客", metric: "有效", value: summary.oldEffectiveDialed },
              { type: "老客", metric: "无效", value: summary.oldDialedCount - summary.oldEffectiveDialed },
            ],
          }],
          xField: ["type", "metric"],
          yField: "value",
          seriesField: "metric",
          bar: { style: { cornerRadius: 4 } },
          color: [COLORS.success, COLORS.danger],
          axes: [
            { orient: "left", title: { visible: true, text: "数量" } },
            { orient: "bottom" },
          ],
          label: { visible: true, position: "top", style: { fontSize: 10, fill: "#999" } },
          tooltip: { visible: true },
          legend: { visible: true, position: "top" },
        } as any,
      };
    }

    // 环形图：切片标签预拼"名称 数量 (百分比)"，中心显示总数
    let effective = 0, ineffective = 0, centerLabel = "总拨号";
    if (filterMode === "total") {
      effective = summary.effectiveDialedCount;
      ineffective = summary.dialedCount - summary.effectiveDialedCount;
    } else if (filterMode === "new") {
      effective = summary.newEffectiveDialed;
      ineffective = summary.newDialedCount - summary.newEffectiveDialed;
      centerLabel = "新客拨号";
    } else {
      effective = summary.oldEffectiveDialed;
      ineffective = summary.oldDialedCount - summary.oldEffectiveDialed;
      centerLabel = "老客拨号";
    }
    const total = effective + ineffective;
    const pct = (v: number) => (total > 0 ? ((v / total) * 100).toFixed(1) : "0.0");

    return {
      centerValue: total,
      centerLabel,
      spec: {
        type: "pie",
        data: [{
          id: "data",
          values: [
            { type: `有效触达 ${effective} (${pct(effective)}%)`, value: effective },
            { type: `无效触达 ${ineffective} (${pct(ineffective)}%)`, value: ineffective },
          ].filter(d => d.value > 0),
        }],
        valueField: "value",
        categoryField: "type",
        // 注意：VChart 1.13 的 innerRadius/outerRadius 是布局半径的比例(0~1)，不是像素：
        // 渲染时 layoutRadius * innerRadius（见 vchart polar.js），默认 outerRadius=0.6
        innerRadius: 0.4,
        color: [COLORS.success, COLORS.danger],
        label: { visible: true, position: "outside" },
        tooltip: { visible: true },
      } as any,
    };
  }, [summary, filterMode]);

  const salesChart1 = useMemo(() => {
    const rows = statData?.rows ?? [];
    if (rows.length === 0) return null;

    const values: { saleName: string; metric: string; value: number }[] = [];
    rows.forEach(r => {
      const x = r.saleName || "-";
      if (filterMode === "total" || filterMode === "compare") {
        values.push({ saleName: x, metric: "总分配", value: r.totalAssigned });
        values.push({ saleName: x, metric: "触达", value: r.totalAssignedTouched });
      }
      if (filterMode === "new" || filterMode === "compare") {
        values.push({ saleName: x, metric: "新客分配", value: r.newAssignedCount });
        values.push({ saleName: x, metric: "新客触达", value: r.newAssignedTouched });
      }
      if (filterMode === "old" || filterMode === "compare") {
        values.push({ saleName: x, metric: "老客分配", value: r.oldAssignedCount });
        values.push({ saleName: x, metric: "老客触达", value: r.oldAssignedTouched });
      }
    });

    return {
      type: "bar",
      data: [{ id: "data", values }],
      xField: ["saleName", "metric"],
      yField: "value",
      seriesField: "metric",
      bar: { style: { cornerRadius: 3 } },
      // 柱顶直接标注数值，无需 hover
      label: { visible: true, position: "top", style: { fontSize: 10, fill: "#999" } },
      color: [COLORS.primary, COLORS.success, COLORS.warning, COLORS.danger, COLORS.purple, COLORS.cyan],
      axes: [
        { orient: "left", title: { visible: true, text: "数量" } },
        { orient: "bottom" },
      ],
      tooltip: { visible: true },
      legend: { visible: true, position: "top" },
    } as any;
  }, [statData, filterMode]);

  const salesChart2 = useMemo(() => {
    const rows = statData?.rows ?? [];
    if (rows.length === 0) return null;

    const values: { saleName: string; metric: string; value: number }[] = [];
    rows.forEach(r => {
      const x = r.saleName || "-";
      if (filterMode === "total" || filterMode === "compare") {
        values.push({ saleName: x, metric: "拨号", value: r.dialedCount });
        values.push({ saleName: x, metric: "有效", value: r.effectiveDialedCount });
      }
      if (filterMode === "new" || filterMode === "compare") {
        values.push({ saleName: x, metric: "新客拨号", value: r.newDialedCount });
        values.push({ saleName: x, metric: "新客有效", value: r.newEffectiveDialed });
      }
      if (filterMode === "old" || filterMode === "compare") {
        values.push({ saleName: x, metric: "老客拨号", value: r.oldDialedCount });
        values.push({ saleName: x, metric: "老客有效", value: r.oldEffectiveDialed });
      }
    });

    return {
      type: "bar",
      data: [{ id: "data", values }],
      xField: ["saleName", "metric"],
      yField: "value",
      seriesField: "metric",
      bar: { style: { cornerRadius: 3 } },
      // 柱顶直接标注数值，无需 hover
      label: { visible: true, position: "top", style: { fontSize: 10, fill: "#999" } },
      color: [COLORS.primary, COLORS.success, COLORS.warning, COLORS.danger, COLORS.purple, COLORS.cyan],
      axes: [
        { orient: "left", title: { visible: true, text: "数量" } },
        { orient: "bottom" },
      ],
      tooltip: { visible: true },
      legend: { visible: true, position: "top" },
    } as any;
  }, [statData, filterMode]);

  // ===== 表格列 =====
  const detailColumns = useMemo(() => {
    const cols: any[] = [
      {
        title: "客户",
        dataIndex: "leadName",
        width: 120,
        render: (v: string, r: CustomerTouchDetailVO) => (
          <div>
            <div style={{ fontWeight: 500 }}>{v || "-"}</div>
            <div style={{ fontSize: 11, color: COLORS.textTertiary }}>{r.leadMobile}</div>
          </div>
        ),
      },
    ];

    if (filterMode !== "total") {
      cols.push({
        title: "类型",
        dataIndex: "newLead",
        width: 70,
        render: (v: number) => (
          <Tag size="small" color={v === 1 ? "orange" : "grey"}>
            {v === 1 ? "新客" : "老客"}
          </Tag>
        ),
      });
    }

    cols.push(
      {
        title: "销售",
        dataIndex: "saleName",
        width: 100,
      },
      {
        title: "部门",
        dataIndex: "teamName",
        width: 120,
      },
      {
        title: "城市",
        dataIndex: "city",
        width: 80,
      },
      {
        title: "通话",
        dataIndex: "callCount",
        width: 80,
        render: (v: number, r: CustomerTouchDetailVO) => (
          <Button
            theme="borderless"
            type="primary"
            size="small"
            onClick={() => handleDrillDown(r)}
          >
            {v}次
          </Button>
        ),
      },
      {
        title: "接通数",
        dataIndex: "connectedCount",
        width: 80,
        render: (v: number, r: CustomerTouchDetailVO) => (
          <Button
            theme="borderless"
            type="primary"
            size="small"
            style={{ color: v > 0 ? COLORS.success : undefined }}
            onClick={() => handleDrillDown(r, true)}
          >
            {v}次
          </Button>
        ),
      },
      {
        title: "未接通数",
        dataIndex: "unconnectedCount",
        width: 90,
        render: (v: number, r: CustomerTouchDetailVO) => (
          <Button
            theme="borderless"
            type="primary"
            size="small"
            style={{ color: v > 0 ? COLORS.danger : undefined }}
            onClick={() => handleDrillDown(r, false)}
          >
            {v}次
          </Button>
        ),
      },
      {
        title: "有效",
        dataIndex: "effectiveCount",
        width: 70,
      },
      {
        title: "有效触达率",
        dataIndex: "touchRate",
        width: 120,
        render: (v: number) => {
          const color = v >= 80 ? COLORS.success : v >= 50 ? COLORS.warning : COLORS.danger;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Progress
                percent={v}
                size="small"
                style={{ width: 60 }}
                stroke={color}
                showInfo={false}
              />
              <span style={{ fontSize: 12, color, fontWeight: 500 }}>{percentText(v)}</span>
            </div>
          );
        },
      },
      {
        title: "时长",
        dataIndex: "avgCallDuration",
        width: 80,
        render: (v: number) => formatDuration(v),
      }
    );

    return cols;
    // appliedReq 必须在依赖里：否则列 render 闭包里的 handleDrillDown/loadCallLogs
    // 会捕获旧渲染的 appliedReq，导致下钻日期停留在初始值（闭包过期）
  }, [filterMode, appliedReq]);

  const callLogColumns = [
    {
      title: "拨打时间",
      dataIndex: "callAt",
      width: 150,
      render: (v: string) => v ? formatDateTime(v) : "-",
    },
    {
      title: "挂断时间",
      dataIndex: "endAt",
      width: 150,
      render: (v: string) => v ? formatDateTime(v) : "-",
    },
    {
      title: "时长",
      dataIndex: "callTimeLong",
      width: 100,
      render: (v: number) => {
        const effective = v != null && v > EFFECTIVE_THRESHOLD_SECONDS;
        return (
          <Tag color={effective ? "green" : "grey"} size="small">
            {formatDuration(v)}
          </Tag>
        );
      },
    },
    {
      title: "状态",
      dataIndex: "status",
      width: 90,
      render: (v: string) => translateStatus(v),
    },
    {
      title: "类型",
      dataIndex: "newLead",
      width: 70,
      render: (v: number) => (
        <Tag size="small" color={v === 1 ? "orange" : "grey"}>
          {v === 1 ? "新客" : "老客"}
        </Tag>
      ),
    },
    {
      title: "录音",
      dataIndex: "recordUploaded",
      width: 80,
      render: (v: boolean, r: LeadCallLogItem) =>
        v && r.recordFile ? (
          <Button
            theme="borderless"
            type="primary"
            icon={<IconPlay />}
            size="small"
            onClick={() => {
              setAudioUrl(r.recordFile);
              setAudioTitle(r.recordName || r.leadName || "录音");
              setAudioFileName(r.recordName || "");
              setAudioVisible(true);
            }}
          >
            播放
          </Button>
        ) : (
          <Text type="tertiary">无</Text>
        ),
    },
  ];

  // ===== 下钻上下文 =====
  // 按 leadId + saleId 联合查找，同一客户挂多个销售时避免显示错行
  const drillRow = drillLead
    ? detailList.find(r => r.leadId === drillLead.leadId && r.saleId === drillLead.saleId)
    : undefined;
  const connectedLabel =
    drillLead?.connected === true ? "仅接通" : drillLead?.connected === false ? "仅未接通" : "全部";

  // ===== 渲染 =====
  return (
    <div style={{ background: COLORS.bgPage, minHeight: "100vh", padding: "16px 24px" }}>
      {/* ===== 页面标题 ===== */}
      {/*
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <Title heading={3} style={{ margin: 0, marginBottom: 4 }}>客户触达分析</Title>
            <Text type="tertiary">
              {appliedReq.callAtStart === appliedReq.callAtEnd
                ? `${appliedReq.callAtStart}`
                : `${appliedReq.callAtStart} ~ ${appliedReq.callAtEnd}`}
            </Text>
          </div>
          <Space>
            <Button icon={<IconRefresh />} loading={statLoading} onClick={() => {
              loadStat(appliedReq);
              loadDetail(appliedReq, detailPage);
            }}>
              刷新
            </Button>
            <Button icon={<IconDownload />} type="warning" theme="solid" onClick={handleExport}>
              导出
            </Button>
          </Space>
        </div>
      </div>
      */}

      {/* ===== 筛选区 ===== */}
      <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: "12px 8px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
          {/* 时间选择 */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <IconCalendar style={{ color: COLORS.textSecondary }} />
            <DatePicker
              type="dateRange"
              value={dateRange}
              onChange={(d: any) => {
                if (!d) return;
                // 起/止反选时自动交换，避免提交后端后查空
                const [s, e] = d;
                if (s && e && dayjs(e).isBefore(dayjs(s), "day")) setDateRange([e, s]);
                else setDateRange(d);
              }}
              size="small"
              style={{ width: 260 }}
            />
          </div>

          {/* 快捷按钮 */}
          <div style={{ display: "flex", gap: 4 }}>
            {[
              { key: "today", label: "今日" },
              { key: "7d", label: "近7天" },
              { key: "30d", label: "近30天" },
              { key: "month", label: "本月" },
            ].map(item => (
              <Button
                key={item.key}
                size="small"
                theme="light"
                onClick={() => handlePreset(item.key as any)}
              >
                {item.label}
              </Button>
            ))}
          </div>

          <Divider layout="vertical" />

          {/* 组织选择 */}
          <TreeSelect
            style={{ width: 180 }}
            size="small"
            value={teamId ?? undefined}
            onChange={(v: any) => {
              setTeamId(v ?? null);
              setSaleId(null);
            }}
            placeholder="选择部门"
            treeData={teamTreeData}
            filterTreeNode
            showClear
          />

          <TreeSelect
            style={{ width: 180 }}
            size="small"
            value={saleId ?? undefined}
            onChange={(v: any) => setSaleId(v != null ? Number(v) : null)}
            placeholder="选择销售"
            treeData={managerTreeData}
            filterTreeNode
            showClear
            leafOnly
          />

          <div style={{ flex: 1 }} />

          {/* 筛选模式 */}
          <Radio.Group
            value={filterMode}
            onChange={(e) => handleFilterModeChange(e.target.value)}
            type="button"
          >
            <Radio value="compare">对比</Radio>
            <Radio value="total">总的</Radio>
            <Radio value="new">新客</Radio>
            <Radio value="old">老客</Radio>

          </Radio.Group>

          <Button type="primary" onClick={handleFilter}>查询</Button>
          <Button onClick={handleReset}>重置</Button>
        </div>
      </Card>

      {/* ===== Tab 切换 ===== */}
      <Tabs
        activeKey={activeTab}
        onChange={(k) => setActiveTab(k as any)}
        type="line"
        style={{ marginBottom: 16 }}
      >
        <TabPane tab="客户维度" itemKey="customer" />
        <TabPane tab="销售维度" itemKey="sales" />
      </Tabs>

      {/* ===== 客户维度 ===== */}
      {activeTab === "customer" && (
        <Spin spinning={statLoading}>
          {/* KPI 卡片 */}
          <KpiSection title="📊 分配触达" cards={kpiData.assign} />
          <KpiSection title="📞 拨号有效触达" cards={kpiData.dialed} />

          {/* 图表：环形图中心叠加总数，不依赖 hover 即可读到全部数字 */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col span={12}>
              <Card title="分配触达分布" bodyStyle={{ padding: 16 }}>
                <div style={{ position: "relative", height: 320, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {customerAssignChart ? (
                    <>
                      <VChart spec={customerAssignChart.spec} options={{ mode: "desktop-browser" }} height={320} />
                      {customerAssignChart.centerValue != null && (
                        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                          <div style={{ fontSize: 26, fontWeight: 700, color: COLORS.textPrimary, lineHeight: 1 }}>
                            {customerAssignChart.centerValue}
                          </div>
                          <div style={{ fontSize: 12, color: COLORS.textTertiary, marginTop: 4 }}>
                            {customerAssignChart.centerLabel}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <Empty description="暂无数据" />
                  )}
                </div>
              </Card>
            </Col>
            <Col span={12}>
              <Card title="拨号有效触达分布" bodyStyle={{ padding: 16 }}>
                <div style={{ position: "relative", height: 320, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {customerDialedChart ? (
                    <>
                      <VChart spec={customerDialedChart.spec} options={{ mode: "desktop-browser" }} height={320} />
                      {customerDialedChart.centerValue != null && (
                        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                          <div style={{ fontSize: 26, fontWeight: 700, color: COLORS.textPrimary, lineHeight: 1 }}>
                            {customerDialedChart.centerValue}
                          </div>
                          <div style={{ fontSize: 12, color: COLORS.textTertiary, marginTop: 4 }}>
                            {customerDialedChart.centerLabel}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <Empty description="暂无数据" />
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        </Spin>
      )}

      {/* ===== 销售维度 ===== */}
      {activeTab === "sales" && (
        <Spin spinning={statLoading}>
          {/* KPI 卡片 */}
          <KpiSection title="📊 分配触达" cards={kpiData.assign} />
          <KpiSection title="📞 拨号有效触达" cards={kpiData.dialed} />

          {/* 图表 */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col span={12}>
              <Card title="各销售分配触达对比" bodyStyle={{ padding: 16 }}>
                <div style={{ height: 320 }}>
                  {salesChart1 ? (
                    <VChart spec={salesChart1} options={{ mode: "desktop-browser" }} height={320} />
                  ) : (
                    <div style={{ height: 320, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Empty description="暂无数据" />
                    </div>
                  )}
                </div>
              </Card>
            </Col>
            <Col span={12}>
              <Card title="各销售拨号有效触达对比" bodyStyle={{ padding: 16 }}>
                <div style={{ height: 320 }}>
                  {salesChart2 ? (
                    <VChart spec={salesChart2} options={{ mode: "desktop-browser" }} height={320} />
                  ) : (
                    <div style={{ height: 320, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Empty description="暂无数据" />
                    </div>
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        </Spin>
      )}

      {/* ===== 明细表格 ===== */}
      <Card title="客户触达明细" style={{ marginBottom: 16 }}>
        <Table<CustomerTouchDetailVO>
          columns={detailColumns}
          dataSource={detailList}
          rowKey={(r) => `${r.leadId}-${r.saleId}`}
          loading={detailLoading}
          pagination={{
            currentPage: detailPage,
            pageSize: PAGE_SIZE,
            total: detailTotal,
            onPageChange: (p) => loadDetail(appliedReq, p),
            showSizeChanger: false,
          }}
          scroll={{ x: 1000 }}
          size="small"
          empty={<Empty description="暂无明细数据" />}
        />
      </Card>

      {/* ===== 通话记录下钻 ===== */}
      <SideSheet
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <IconPhone />
            <span>{drillLead?.leadName || "通话记录"}</span>
            <Tag size="small" color={drillLead?.connected === true ? "green" : drillLead?.connected === false ? "red" : "grey"}>
              {connectedLabel}
            </Tag>
          </div>
        }
        visible={drillLead != null}
        onCancel={() => setDrillLead(null)}
        width={800}
        bodyStyle={{ padding: 16 }}
      >
        {drillLead && (
          <Card style={{ marginBottom: 12 }} bodyStyle={{ padding: 12, background: "var(--semi-color-fill-0)" }}>
            <Descriptions
              size="small"
              row
              data={[
                { key: "销售", value: drillRow?.saleName || "-" },
                { key: "部门", value: drillRow?.teamName || "-" },
                { key: "城市", value: drillRow?.city || "-" },
                { key: "通话", value: `${drillRow?.callCount || 0}次` },
                { key: "接通", value: `${drillRow?.connectedCount || 0}次` },
                { key: "未接通", value: `${drillRow?.unconnectedCount || 0}次` },
                { key: "有效", value: `${drillRow?.effectiveCount || 0}次` },
              ]}
            />
          </Card>
        )}
        <Table<LeadCallLogItem>
          columns={callLogColumns}
          dataSource={callLogs}
          rowKey="id"
          loading={callLogsLoading}
          pagination={{
            currentPage: callLogsPage,
            pageSize: CALL_LOG_PAGE_SIZE,
            total: callLogsTotal,
            onPageChange: (p) => drillLead && loadCallLogs(drillLead.leadId, drillLead.saleId, p, drillLead.connected),
            showSizeChanger: false,
          }}
          scroll={{ x: 700 }}
          size="small"
          empty={<Empty description="无通话记录" />}
        />
      </SideSheet>

      <AudioPlayerModal
        visible={audioVisible}
        url={audioUrl}
        title={audioTitle}
        fileName={audioFileName}
        onClose={() => { setAudioVisible(false); setAudioUrl(""); setAudioFileName(""); }}
      />
    </div>
  );
}
